/**
 * The read-only record of what the applicant submitted.
 *
 * Two surfaces read it. The Review and submit section shows the whole
 * application as one document before it is filed, and every applicant
 * section shows its own values back once the application is frozen — the
 * same rows either way, so what the client checks before submitting is
 * literally what they see afterwards.
 *
 * Pure by design: no React, no fetching. The values come from the step
 * payloads the hub already holds, and the wallets from the list it already
 * loads for the wallet forms.
 */

import {
    type ApplicantReviewRow,
    reviewFieldLabel,
    reviewRowsForPayload
} from "@solomon-apps/shared/src/onboarding/applicantReview";

import type { OnboardingApplicationStatus, OnboardingStepName, OnboardingStepView } from "@/services/clientOnboarding";

import { STEP_TO_SUBSECTION, stepDisplayLabel } from "./stepReadiness";
import type { SubsectionId } from "./types";
import type { WalletEntry } from "./walletEntries";

export type ReviewRow = ApplicantReviewRow;

export interface ReviewSection {
    subsectionId: SubsectionId;
    stepName: OnboardingStepName;
    title: string;
    rows: ReviewRow[];
}

/**
 * Applicant sections in the order the rail shows them. The agreements are
 * excluded: they carry no applicant-entered values, only envelope state,
 * which their own panels render. Sanctions screening is excluded because
 * Solomon runs it.
 */
export const REVIEWED_STEPS: readonly OnboardingStepName[] = [
    "use_case_and_intent",
    "legal_entity",
    "tax_identification",
    "beneficial_ownership",
    "source_of_funds",
    "wallet_declarations",
    "signatory_authority"
];

// Field labels, value spellings and payload-to-rows live in shared, so the
// issuer console reads an applicant's answers under the same labels.
export { reviewFieldLabel, reviewRowsForPayload };

/**
 * The wallet section's rows. Verified wallets live on Participant Account
 * records, not in the step payload (the step carries none), so they come
 * from the list the hub loads for the wallet forms. Labels follow the
 * rail's slot titles, so the record reads the way the section did.
 */
export function reviewRowsForWallets(entries: readonly WalletEntry[]): ReviewRow[] {
    return entries.map((entry, index) => ({
        label: entry.label.trim() || `Wallet ${index + 1}`,
        value: entry.wallet ? `${entry.wallet.address} · Verified` : "Not verified"
    }));
}

/** Every applicant section with something on file, in rail order. */
export function buildReviewSections(
    steps: readonly OnboardingStepView[],
    walletEntries: readonly WalletEntry[] = []
): ReviewSection[] {
    const byName = new Map(steps.map((step) => [step.stepName, step]));
    return REVIEWED_STEPS.flatMap((stepName) => {
        const subsectionId = STEP_TO_SUBSECTION[stepName];
        if (!subsectionId) return [];
        const step = byName.get(stepName);
        if (!step) return [];
        return [
            {
                subsectionId,
                stepName,
                title: stepDisplayLabel(stepName),
                rows:
                    stepName === "wallet_declarations"
                        ? reviewRowsForWallets(walletEntries)
                        : reviewRowsForPayload(step.payload)
            }
        ];
    });
}

/**
 * What a frozen applicant section says above its saved values. The section
 * stays frozen for the rest of the application's life, so the line has to
 * say where the application actually is rather than always "in review".
 */
export function frozenSectionCopy(status: OnboardingApplicationStatus): string {
    switch (status) {
        case "awaiting_agreements":
            return "Submitted. Your application is approved and the agreements are out for signature.";
        case "approved":
            return "Submitted. Your application is approved.";
        case "rejected":
            return "Submitted. This application was declined.";
        case "draft":
        case "in_review":
        default:
            return "Submitted and locked while Solomon reviews your application.";
    }
}

/** The Review and submit lead once the application has been filed. */
export function submittedReviewCopy(status: OnboardingApplicationStatus): string {
    switch (status) {
        case "awaiting_agreements":
            return "This is what you submitted. Your application is approved and the agreements are out for signature.";
        case "approved":
            return "This is what you submitted. Your application is approved.";
        case "rejected":
            return "This is what you submitted. This application was declined.";
        case "draft":
        case "in_review":
        default:
            return "This is what you submitted. Solomon reviews it and sends the agreements for signature once it is approved.";
    }
}

/**
 * Whether an applicant section still accepts edits.
 *
 * A submitted application is a snapshot the reviewer is reading, so it
 * freezes. The one exception is the authorized signatory while the
 * agreements are out: that is where a wrong signing address is corrected,
 * and the issuer's resend renders from the corrected snapshot. Mirrors the
 * API's own carve-out in `OnboardingStepService.submit`.
 */
export function isSectionEditable(
    status: OnboardingApplicationStatus | undefined,
    subsectionId: SubsectionId
): boolean {
    if (status === undefined || status === "draft") return true;
    return status === "awaiting_agreements" && subsectionId === "signatory-authority";
}

/** Whether the agreements have been sent, which is what unlocks their panels. */
export function agreementsUnlocked(status: OnboardingApplicationStatus | undefined): boolean {
    return status === "awaiting_agreements" || status === "approved";
}
